import { isNumber } from 'lodash';
import styled from 'styled-components';
import { plural } from '@frameio/i18n';
import { getPlanRate } from '@frameio/plan-management/plans/utils/getPlanRate';
import type { ThemeColorsBackground, ThemeColorsMeta } from '@frameio/vapor';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Dialog,
  Separator,
  Stack,
  Text,
  textStyles,
  theme,
} from '@frameio/vapor';
import { IconPersonStack } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ExistingGuestDialog';

TEXT.DEFINE({
  /** Text for the label of the badge that displays the guest avatar in the existing guest dialog */
  badgeLabel: 'Guest avatar',
  /** Text for the label of the dialog that displays the existing guest dialog */
  dialogLabel: 'Existing Guest',
  /** Text for the title of the single guest dialog */
  singleTitle: 'Move Guest to This Project?',
  /** Text for the title of the multiple guest dialog */
  bulkTitle: (num: number) =>
    plural(num, {
      one: `Move ${num} Guest to This Project?`,
      other: `Move ${num} Guests to This Project?`,
    }),
  /** Text for the description of the existing guest dialog */
  description:
    'Guests can only access one project at a time. Adding this Guest will remove them from their existing project.',
  /** Text for the description of the multiple guest dialog */
  bulkDescription:
    'Guests can only access one project at a time. Adding these Guests will remove them from their existing project.',
  /** Text for the button that adds the guest as a member in the existing guest dialog */
  addAsMember: 'Add as Member',
  /** Text for the button that adds the guests as members in the existing guest dialog */
  addAsMembers: 'Add as Members',
  /** Text for the button that moves the guest in the existing guest dialog */
  moveSingleGuest: 'Move Guest',
  /** Text for the button that moves the guests in the existing guest dialog */
  moveMultipleGuests: 'Move Guests',
  /** Text for the upgrade rate of the existing guest dialog. Rate example: $10/mo */
  upgradeRate: (rate: string) => `${rate} (plus tax) per new Member`,
});
export type ExistingGuestDialogProps = {
  avatarBackground?: string;
  avatarSrc?: string;
  isShowing: boolean;
  name?: string;
  numGuests?: number;
  onInviteAsMember: () => void;
  onMoveGuests: () => void;
  onRequestClose: () => void;
  planPeriod?: string;
  subscriptionCost?: number;
  zIndex?: number;
};
const GuestAvatar = ({
  background,
  href,
  name,
  numGuests,
}: {
  background: string;
  name: string;
  href?: string;
  numGuests?: number;
}) => (
  <BadgeContainer>
    {numGuests ? (
      <IconContainer>
        <IconShadow>
          <MultiGuestIcon size={24} />
        </IconShadow>
      </IconContainer>
    ) : (
      <Badge
        backgroundColor="notice"
        borderColor="panel"
        borderSize={2}
        content={
          (
            /** @todo (VAPR-1858): Casting to 'any' due to type mismatch in `content` prop.
             * Temporary fix until component's types are corrected.
             */
            <IconPersonStack color={theme.colors.overlay.high} size={24} />
          ) as any
        }
        isShowing={true}
        label={TEXT.badgeLabel}
        placement="bottom-right"
        size={24}
      >
        <Avatar
          alt={name}
          background={background as ThemeColorsBackground | ThemeColorsMeta}
          size={48}
          src={href}
        />
      </Badge>
    )}
  </BadgeContainer>
);
const Divider = () => (
  <SeparatorContainer>
    <Separator />
  </SeparatorContainer>
);
export function ExistingGuestDialog({
  avatarBackground,
  avatarSrc,
  isShowing,
  subscriptionCost,
  planPeriod,
  name,
  numGuests,
  onInviteAsMember,
  onMoveGuests,
  onRequestClose,
  zIndex,
}: ExistingGuestDialogProps) {
  return (
    <StyledDialog
      aria-labelledby={TEXT.dialogLabel}
      isShowing={isShowing}
      onRequestClose={onRequestClose}
      zIndex={zIndex}
    >
      <Box paddingTop={24} paddingX={24}>
        <Stack alignX="center">
          <GuestAvatar
            background={avatarBackground}
            href={avatarSrc}
            name={name}
            numGuests={numGuests}
          />
          <StyledTitle textAlign="center" variant="body">
            {numGuests ? TEXT.bulkTitle(numGuests) : TEXT.singleTitle}
          </StyledTitle>
          <StyledDescription>
            {numGuests && numGuests > 1
              ? TEXT.bulkDescription
              : TEXT.description}
          </StyledDescription>
          <ButtonContainer>
            <Button block={true} onClick={onMoveGuests} size="small">
              {numGuests ? TEXT.moveMultipleGuests : TEXT.moveSingleGuest}
            </Button>
          </ButtonContainer>
        </Stack>
      </Box>
      <Divider />
      <Box display="flex" justifyContent="center" paddingBottom={16}>
        <ButtonContainer>
          <Button
            block={true}
            color="primary"
            onClick={onInviteAsMember}
            size="small"
          >
            {numGuests ? TEXT.addAsMembers : TEXT.addAsMember}
          </Button>
          {isNumber(subscriptionCost) && planPeriod ? (
            <Text
              color="tertiary"
              fontWeight="regular"
              textAlign="center"
              variant="caption"
            >
              {TEXT.upgradeRate(getPlanRate(planPeriod, subscriptionCost))}
            </Text>
          ) : null}
        </ButtonContainer>
      </Box>
    </StyledDialog>
  );
}
const IconContainer = styled.div`
  height: 40px;
  position: relative;
  width: 40px;
`;
const BadgeContainer = styled.div`
  padding-bottom: ${theme.spaces[12]};
`;
const IconShadow = styled.div`
  background: ${theme.colors.background.interactive.notice};
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px ${theme.colors.background.interactive.notice};
  color: ${theme.colors.text.interactive.notice};
  height: 100%;
  position: absolute;
  width: 100%;
`;
const MultiGuestIcon = styled(IconPersonStack)`
  left: 50%;
  position: relative;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
`;
const ButtonContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[8]};
  width: 204px;
`;
const StyledDialog = styled(Dialog)`
  width: 252px;
`;
const SeparatorContainer = styled.div`
  padding: ${theme.spaces[12]} 0;
`;
const StyledTitle = styled(Text)`
  padding-bottom: ${theme.spaces[4]};
`;
const StyledDescription = styled(Text)`
  ${textStyles.secondary}
  color: ${theme.colors.text.tertiary};
  padding-bottom: ${theme.spaces[16]};
  text-align: center;
`;
